import { Box, Card, Divider, Icons, Stack, Typography } from '@healthinal/ui';
import { differenceInMilliseconds, format } from 'date-fns';
import {
  ClinicalReportDto,
  LabReportDto,
  LabReportDtoSpecies,
  LabReportMutationDtoEnzyme,
  LabReportMutationDtoMutationStatus,
  LabReportResistanceDtoResistance,
  LabReportResistanceDtoResistanceName,
  TherapyDto,
} from '../../../../../api/generated/model';
import { SubCard } from '../../../../../components/SubCard.tsx';
import { theme } from '../../../../../theme.ts';
import { getClinicalMaterialSummaryStrings, getOrganName } from '../../../../../utils/clinicalReport.ts';
import { formatDateWithGermanMonth } from '../../../../../utils/date.ts';
import {
  getDetectionMethodWithMaterialSummaryStrings,
  getEnzymeName,
  getMutationStatusName,
  getResistanceName,
  getResistanceNameName,
  getSpeciesName,
} from '../../../../../utils/labReport.tsx';

export function DiagnosisSummaryCard({
  therapy,
  clinicalReport,
  labReports,
}: {
  therapy: TherapyDto;
  clinicalReport: ClinicalReportDto;
  labReports: LabReportDto[];
}) {
  const sortedLabReports = [...labReports].sort(
    (a, b) =>
      differenceInMilliseconds(b.samplingDate, a.samplingDate) ||
      differenceInMilliseconds(b.created ?? 0, a.created ?? 0),
  );

  const lapReportsByDate = sortedLabReports
    .map((l) => ({
      date: l.samplingDate,
      created: l.created ?? 0,
      mutations: l.mutations,
      resistances: l.resistances,
      species: l.species,
    }))
    .sort((a, b) => differenceInMilliseconds(b.date, a.date) || differenceInMilliseconds(b.created, a.created));

  const species = new Set<LabReportDtoSpecies>(lapReportsByDate.flatMap((r) => r.species ?? []));

  const newestResistances = new Map<LabReportResistanceDtoResistanceName, LabReportResistanceDtoResistance>();
  const newestMutations = new Map<LabReportMutationDtoEnzyme, LabReportMutationDtoMutationStatus>();
  lapReportsByDate.forEach((report) => {
    report.resistances.forEach((reportResistance) => {
      if (!newestResistances.has(reportResistance.resistanceName)) {
        newestResistances.set(reportResistance.resistanceName, reportResistance.resistance);
      }
    });

    report.mutations.forEach((mutation) => {
      if (!newestMutations.has(mutation.enzyme)) {
        newestMutations.set(mutation.enzyme, mutation.mutationStatus);
      }
    });
  });

  const showEmptyState =
    !therapy.startPhase1 &&
    !therapy.end &&
    newestMutations.size == 0 &&
    !clinicalReport.materials &&
    species.size == 0 &&
    !clinicalReport.organs;

  const lastUpdate = Math.max(
    lapReportsByDate.length > 0 ? Date.parse(lapReportsByDate[0].date) : 0,
    clinicalReport.updated ? Date.parse(clinicalReport.updated) : 0,
  );

  const latestLabReport = sortedLabReports[0];

  return (
    <Card>
      <Stack>
        <Typography level={'title-lg'}>Diagnose</Typography>
        <Typography level={'body-sm'}>Letztes Update: {lastUpdate > 0 && format(lastUpdate, 'dd.MM.yyyy')}</Typography>
      </Stack>
      <Divider />
      {showEmptyState ? (
        <Stack alignItems={'center'} gap={1} p={3} gridColumn={2}>
          <Icons.Description />
          <Typography level="body-sm">Noch keine Daten vorhanden.</Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            gap: 2,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            [theme.breakpoints.down(1200)]: {
              gridTemplateColumns: '1fr 1fr',
            },
          }}>
          <SubCard>
            <Typography level={'body-sm'}>Befallene Organe</Typography>
            <div>
              {clinicalReport.organs?.map((organ) => (
                <Typography level={'title-md'} key={organ}>
                  {getOrganName(organ, clinicalReport.otherOrgan)}
                </Typography>
              ))}
            </div>
          </SubCard>
          <SubCard>
            <Typography level={'body-sm'}> Material | Nachweismethode | Resultat</Typography>
            <div>
              {labReports.length > 0 ? getDetectionMethodWithMaterialSummaryStrings(latestLabReport).map((summary, index) => (
                <Typography key={`${summary}-${index}`} level="title-md">
                  {summary}
                </Typography>
              )) :

                getClinicalMaterialSummaryStrings(clinicalReport.materials).map((summary) => (
                  <Typography key={summary} level={'title-md'}>
                    {summary}
                  </Typography>
                ))}
            </div>
          </SubCard>
          <SubCard>
            <Typography level={'body-sm'}>Mutationen</Typography>
            <div>
              {Array.from(newestMutations).map(([key, value]) => (
                <Typography level={'title-md'} key={key}>
                  {getEnzymeName(key)}: {getMutationStatusName(value)}
                </Typography>
              ))}
            </div>
          </SubCard>
          <SubCard>
            <Typography level={'body-sm'}>Therapie</Typography>
            <Typography level={'title-md'}>
              {formatDateWithGermanMonth(therapy.startPhase1)} {therapy.end && `- ${formatDateWithGermanMonth(therapy.end)}`}
            </Typography>
          </SubCard>
          <SubCard>
            <Typography level={'body-sm'}>Spezies</Typography>
            <div>
              {Array.from(species).map((s) => (
                <Typography level={'title-md'} key={s}>
                  {getSpeciesName(s)}
                </Typography>
              ))}
            </div>
          </SubCard>
          <SubCard>
            <Typography level={'body-sm'}>Resistenzen</Typography>
            <div>
              {Array.from(newestResistances).map(([key, value]) => (
                <Typography level={'title-md'} key={key}>
                  {getResistanceNameName(key)}: {getResistanceName(value)}
                </Typography>
              ))}
            </div>
          </SubCard>
        </Box>
      )}
    </Card>
  );
}
